import { useEffect, useMemo, useState } from "react"
import toast from "react-hot-toast"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Switch,
  Textarea,
} from "@aix/ui"

import { trpc } from "../lib/trpc"

interface EditableSeoSettings {
  brand?: string
  brandSuffix?: string
  voiceRules?: string
  bannedPhrases?: string[]
  quality?: {
    requireUseCases?: boolean
    minExamples?: number
    minFaq?: number
    minInternalLinks?: number
    minWords?: number
    minMedia?: number
    requireHeroMedia?: boolean
    requireExampleMedia?: boolean
    requireEvidence?: boolean
    requireKnownEvidenceRights?: boolean
    requireKnownMediaRights?: boolean
    requireMediaProvenance?: boolean
    requireMediaDimensions?: boolean
    requireUpdatedAt?: boolean
    requireAnswerFirst?: boolean
    requireAudienceFit?: boolean
    requireLimitations?: boolean
    requireComparisonMethodology?: boolean
  }
}

const NUMBER_REQUIREMENTS: readonly [keyof NonNullable<EditableSeoSettings["quality"]>, string][] = [
  ["minWords", "Minimum useful words"],
  ["minExamples", "Minimum examples"],
  ["minFaq", "Minimum FAQ answers"],
  ["minInternalLinks", "Minimum internal links"],
  ["minMedia", "Minimum media assets"],
]

const BOOLEAN_REQUIREMENTS: readonly [keyof NonNullable<EditableSeoSettings["quality"]>, string][] = [
  ["requireUseCases", "Require use-case bullets"],
  ["requireHeroMedia", "Require hero media"],
  ["requireExampleMedia", "Require media for each example"],
  ["requireEvidence", "Require supporting evidence"],
  ["requireKnownEvidenceRights", "Require reviewed evidence rights"],
  ["requireKnownMediaRights", "Require publishable media rights"],
  ["requireMediaProvenance", "Require media provenance"],
  ["requireMediaDimensions", "Require media dimensions"],
  ["requireUpdatedAt", "Require a last-updated date"],
  ["requireAnswerFirst", "Require a short answer (≤150 words) and 2+ key takeaways"],
  ["requireAudienceFit", "Require best-for and not-for lists"],
  ["requireLimitations", "Require costs, limits, or common mistakes"],
  ["requireComparisonMethodology", "Require methodology on comparison pages"],
]

export default function SEOProductSettings({ productKey }: { productKey: string }) {
  const settings = trpc.seo.products.operatorConfig.useQuery({ productKey })
  const [draft, setDraft] = useState<EditableSeoSettings | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const utils = trpc.useUtils()

  useEffect(() => {
    if (settings.data) setDraft(settings.data.config)
  }, [settings.data])

  const changedFields = useMemo(() => {
    if (!settings.data || !draft) return []
    const fields: (keyof EditableSeoSettings)[] = ["brand", "brandSuffix", "voiceRules", "bannedPhrases", "quality"]
    return fields.filter((field) => JSON.stringify(settings.data.config[field]) !== JSON.stringify(draft[field]))
  }, [draft, settings.data])

  const saveMutation = trpc.seo.products.updateOperatorConfig.useMutation({
    onError: (error) => toast.error(error.message || "Product settings save failed"),
    onSuccess: (result) => {
      toast.success(
        `Saved ${result.changedFields.length} setting group(s); ${result.invalidatedPages} page(s) must be gated again`,
      )
      setConfirmOpen(false)
      void utils.seo.products.operatorConfig.invalidate({ productKey })
      void utils.seo.products.list.invalidate()
      void utils.seo.pages.invalidate()
      void utils.seo.cockpit.invalidate()
    },
  })

  if (settings.isLoading || !draft) return <p className="text-sm text-muted-foreground">Loading product settings…</p>
  if (settings.error)
    return <p className="text-sm text-destructive">Product settings unavailable: {settings.error.message}</p>

  const patchQuality = (patch: NonNullable<EditableSeoSettings["quality"]>) => {
    setDraft((current) => (current ? { ...current, quality: { ...current.quality, ...patch } } : current))
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Safe product settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">
          These settings control editorial quality gates only. Worker bindings, credentials, provider spend, page
          families, and deployment configuration are intentionally unavailable here.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="seo-product-brand">Brand name</Label>
            <Input
              id="seo-product-brand"
              value={draft.brand ?? ""}
              onChange={(event) => setDraft({ ...draft, brand: event.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="seo-product-title-suffix">Title suffix</Label>
            <Input
              id="seo-product-title-suffix"
              value={draft.brandSuffix ?? ""}
              onChange={(event) => setDraft({ ...draft, brandSuffix: event.target.value })}
            />
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="seo-product-voice-rules">Voice rules</Label>
          <Textarea
            id="seo-product-voice-rules"
            rows={4}
            value={draft.voiceRules ?? ""}
            onChange={(event) => setDraft({ ...draft, voiceRules: event.target.value })}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="seo-product-banned-phrases">Banned phrases</Label>
          <Textarea
            id="seo-product-banned-phrases"
            rows={4}
            value={(draft.bannedPhrases ?? []).join("\n")}
            onChange={(event) =>
              setDraft({
                ...draft,
                bannedPhrases: event.target.value
                  .split("\n")
                  .map((value) => value.trim())
                  .filter(Boolean),
              })
            }
          />
          <span className="text-[11px] text-muted-foreground">One phrase per line.</span>
        </div>

        <section className="space-y-3 rounded-md border border-border p-3">
          <div>
            <h3 className="text-sm font-semibold">Publish-ready requirements</h3>
            <p className="text-xs text-muted-foreground">
              Changing these rules does not edit, approve, or publish any page. Existing pages must be gated again.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {NUMBER_REQUIREMENTS.map(([field, label]) => (
              <div key={field} className="space-y-1">
                <Label htmlFor={`seo-quality-${field}`}>{label}</Label>
                <Input
                  id={`seo-quality-${field}`}
                  type="number"
                  min={0}
                  value={Number(draft.quality?.[field] ?? 0)}
                  onChange={(event) => patchQuality({ [field]: event.target.valueAsNumber || 0 })}
                />
              </div>
            ))}
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {BOOLEAN_REQUIREMENTS.map(([field, label]) => (
              <div key={field} className="flex items-center justify-between gap-3 rounded bg-muted p-2 text-xs">
                <span>{label}</span>
                <Switch
                  aria-label={label}
                  checked={Boolean(draft.quality?.[field])}
                  onCheckedChange={(checked) => patchQuality({ [field]: checked })}
                />
              </div>
            ))}
          </div>
        </section>

        <div className="flex flex-wrap items-center gap-2">
          <Button disabled={changedFields.length === 0} onClick={() => setConfirmOpen(true)}>
            Review settings change
          </Button>
          {changedFields.length ? (
            <span className="text-xs text-muted-foreground">Changed: {changedFields.join(", ")}</span>
          ) : (
            <span className="text-xs text-muted-foreground">No unsaved changes.</span>
          )}
        </div>

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Save SEO product settings?</AlertDialogTitle>
              <AlertDialogDescription>
                This changes future page validation for {productKey}. Draft and gated pages will return to Draft and
                must pass the updated rules again. Live immutable releases are unchanged. Changed groups:{" "}
                {changedFields.join(", ")}.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (!settings.data) return
                  saveMutation.mutate({
                    productKey,
                    expectedUpdatedAt: settings.data.updatedAt.toISOString(),
                    config: draft,
                  })
                }}
              >
                Save settings
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  )
}
